import {
  World,
  createWorld,
  addCircle,
  addBox,
  step,
  applyImpulse,
  applyForce,
  LAYER_BALL,
  LAYER_NONE,
  LAYER_PLAYER,
  LAYER_ALL,
  length2,
} from "./solver";

export const COURT_W = 1200;
export const COURT_H = 680;
export const WALL_T = 20;
export const GOAL_H = 230;
export const GOAL_DEPTH = 40;
export const MARGIN = 60;

export interface MapConfig {
  id: string;
  name: string;
  courtW: number;
  courtH: number;
  goalH: number;
}

export const MAPS: MapConfig[] = [
  { id: "classic", name: "CLÁSICO", courtW: 1560, courtH: 680, goalH: 230 },
  { id: "big", name: "GRANDE", courtW: 2040, courtH: 820, goalH: 260 },
  { id: "small", name: "PEQUEÑO", courtW: 1180, courtH: 560, goalH: 200 },
  { id: "wide-goals", name: "PORTERÍA XL", courtW: 1560, courtH: 680, goalH: 340 },
];

export function mapById(id: string): MapConfig {
  return MAPS.find((m) => m.id === id) ?? MAPS[0];
}

export interface MatchRules {
  /** Goles para ganar. 0 = ilimitado. */
  goalLimit: number;
  /** Duración en segundos. 0 = ilimitado. */
  timeLimit: number;
}

export function defaultRules(): MatchRules {
  return { goalLimit: 3, timeLimit: 180 };
}

export const PLAYER_R = 22;
export const BALL_R = 16;
export const PLAYER_MASS = 1;
export const BALL_MASS = 0.35;

export const PLAYER_MAX_SPEED = 360;
export const PLAYER_DRAG = 2.8;
export const PLAYER_ACCEL_FORCE = PLAYER_MAX_SPEED * PLAYER_DRAG;

export const BALL_DRAG = 0.8;
/** Paso fijo del sim (60 Hz), igual en cliente y servidor. Se usa para calibrar
 *  la rapidez de saque a la distancia arco-a-arco bajo el arrastre discreto. */
export const SIM_DT = 1 / 60;
/** Tope de rapidez del balón. Alto a propósito: el saque a máxima posesión debe
 *  cruzar la cancha entera de arco a arco (incluido el mapa más grande) sin que
 *  este clamp lo recorte. */
export const BALL_MAX_SPEED = 1900;
export const CONTACT_PAD = 8;
/** Holgura extra (px) sobre el contacto para considerar que un jugador "tiene"
 *  el balón: mientras esté dentro acumula posesión. */
export const POSSESSION_RANGE_PAD = 22;
/** Radio (centro-a-centro) de POSESIÓN: dentro de él el balón "es tuyo" y la
 *  carga sube. Generoso a propósito — conducir no debería exigir puntería. */
export const POSSESSION_RANGE = PLAYER_R + BALL_R + CONTACT_PAD + POSSESSION_RANGE_PAD;
/**
 * Radio (centro-a-centro) de PATADA. Más corto que el de posesión.
 *
 * Antes eran el mismo número, y eso mezclaba dos cosas distintas: llevar el
 * balón (que debe perdonar) y golpearlo (que debe pedir estar encima). Con el
 * radio único se pateaba desde tan lejos que el balón salía "sin tocarlo", y a
 * la vez la carga se cortaba en cuanto la pelota se separaba un pelo.
 */
export const KICK_RANGE_PAD = 4;
export const KICK_RANGE = PLAYER_R + BALL_R + CONTACT_PAD + KICK_RANGE_PAD;
/**
 * Radio dentro del cual un RIVAL disputa el balón: si lo toca alguien más, tu
 * carga se cancela.
 *
 * Cargar cuatro segundos mientras el otro te empuja la pelota no es posesión,
 * es un premio por estar cerca. Se usa el radio de posesión: si él también "la
 * tiene", nadie la tiene.
 */
export const CONTEST_RANGE = POSSESSION_RANGE;
/**
 * Segundos de posesión continua para alcanzar la fuerza máxima de patada.
 *
 * Cuatro y no cinco: cinco segundos con el balón pegado al disco es una eternidad
 * en una cancha con rivales encima, así que la carga máxima no se veía casi nunca
 * y el tramo final del aro no comunicaba nada.
 */
export const POSSESSION_TIME_MAX = 4;
/**
 * Segundos en los que el aro de carga CRECE. A partir de aquí ya no crece: se
 * enciende el brillo (ver `CHARGE_GLOW_TIME`) hasta llegar al máximo.
 *
 * Separar "crece" de "está lleno" es lo que hace legible la carga: el tamaño
 * dice cuánto llevas, y el brillo dice que ya está — dos señales distintas para
 * dos cosas distintas, en vez de un aro que crece y hay que medir a ojo.
 */
export const CHARGE_GROW_TIME = 3;
/** Segundos de la fase de brillo (de `CHARGE_GROW_TIME` a `POSSESSION_TIME_MAX`). */
export const CHARGE_GLOW_TIME = POSSESSION_TIME_MAX - CHARGE_GROW_TIME;
/** Rapidez de saque (px/s) con 0 s de posesión: el toque mínimo (no tan flojo). */
export const KICK_SPEED_MIN = 400;
/** Fracción de la cancha (arco-a-arco) que alcanza el saque MÁXIMO. <1 ⇒ el
 *  máximo llega casi al arco contrario pero ya no lo cruza con sobra (fuerza
 *  máxima reducida respecto a "cruzar de sobra"). */
export const KICK_CROSS_MARGIN = 0.95;
/** Se conserva para el anillo de "carga"/posesión del HUD (radio del aro). */
export const CHARGE_RANGE_PAD = 6;
/**
 * Radio del CÍRCULO DE SAQUE (el que se dibuja en el centro del campo).
 *
 * Es a la vez decorado y regla: tras un gol, el equipo que lo encajó saca, y
 * mientras tanto el otro no puede entrar aquí (además de que cada uno se queda
 * en su mitad). Un círculo pintado que no significa nada es peor que no
 * pintarlo, así que el número es UNO solo — el mismo que dibuja la cancha y el
 * que aplica la regla.
 */
export const KICKOFF_RADIUS = 150;
export const POST_R = 12;
export const GOAL_FLASH_DURATION = 3;
/**
 * Cuánto dura la celebración del gol.
 *
 * Cabe de sobra en la pausa de saque (`GOAL_FLASH_DURATION`, 3 s). Estaba en 1 s
 * y se quedaba corta: un gol es lo único que pasa en un partido y merece más que
 * un parpadeo. (Además duraba mucho menos de lo que decía este número — se
 * consumía en el replay de red; ver `Snapshot.goalAnim`.)
 */
export const GOAL_ANIM_DURATION = 1.5;

export type GameEvent = "kick" | "goal" | "post" | "bounce" | "chargeStart" | "chargeStop" | "matchEnd" | "overtime";

export interface KickInfo {
  x: number;
  y: number;
  nx: number;
  ny: number;
  intensity: number;
  active: boolean;
}

function makeKickInfo(): KickInfo {
  return { x: 0, y: 0, nx: 0, ny: 0, intensity: 0, active: false };
}

/** Equipo de un slot: 0 = rojo (ataca a la derecha), 1 = turquesa (ataca a la
 *  izquierda). Los espectadores NO ocupan slot en el sim (viven en la sala). */
export type TeamId = 0 | 1;

export interface GameState {
  world: World;
  /** Cuerpo del jugador local (slot 0). Alias de compatibilidad de `players[0]`. */
  player: number;
  /** Cuerpo del segundo jugador (slot 1). -1 si la partida es de 1 jugador.
   *  Alias de compatibilidad de `players[1]`. */
  player2: number;
  /** Cuerpo de cada slot (fuente de verdad, tamaño N). Índices en `world`. */
  players: number[];
  /**
   * ¿Hay alguien de verdad en este slot? (tamaño N).
   *
   * Una sala de 8 tiene 8 cuerpos reservados desde que se crea, pero mientras
   * no haya 8 personas los demás no son jugadores: eran discos parados en la
   * cancha que estorbaban al balón y se leían como "bots de relleno". Un slot
   * inactivo se aparca fuera del campo, deja de colisionar (`mask = 0`), no
   * consume input y no se dibuja. Ver `setSlotActive`.
   */
  active: boolean[];
  /** Equipo de cada slot (rojo/turquesa), tamaño N. Fija spawn y color. */
  teams: TeamId[];
  ball: number;
  posts: [number, number, number, number];
  score: [number, number];
  paused: boolean;
  /* --- control por slot (fuente de verdad, tamaño N) --- */
  /** Ratio de posesión 0..1 (posesión / POSSESSION_TIME_MAX), para el aro del HUD. */
  charges: number[];
  /** true mientras el jugador "tiene" el balón (dentro de POSSESSION_RANGE). */
  chargings: boolean[];
  /** Segundos de posesión continua por slot (se resetea al perder el balón o patear). */
  possession: number[];
  /** Estado previo de la tecla de patada por slot (para detectar el flanco de subida). */
  kickHeld: boolean[];
  /**
   * ¿Queda patada por gastar en esta pulsación? (tamaño N).
   *
   * Se arma al pulsar y se gasta al golpear. Es lo que convierte "mantener" en
   * un disparo con espera —golpea en cuanto la alcances— en vez de en una
   * ráfaga de toques por tick.
   */
  kickArmed: boolean[];
  kickPulses: number[];
  kickInfos: KickInfo[];
  goalAnim: number;
  goalSide: 0 | 1 | null;
  resetTimer: number;
  events: GameEvent[];
  lastBallSpeed: number;
  /** Dimensiones activas del mapa (pueden variar entre partidas). */
  courtW: number;
  courtH: number;
  goalH: number;
  mapId: string;
  /** Reglas del partido. */
  rules: MatchRules;
  matchTime: number;
  matchOver: boolean;
  /** Equipo ganador cuando matchOver es true. */
  winner: 0 | 1 | null;
  /**
   * Prórroga a gol de oro (GP-1): se entra al agotarse el tiempo con empate, y
   * el siguiente gol termina el partido. Sin esto, un partido igualado acababa
   * en tablas — que es no decidir nada, justo lo contrario de para lo que se
   * juega un partido.
   */
  overtime: boolean;
  /**
   * Equipo con derecho al SAQUE, o `null` si no hay restricción.
   *
   * Mientras está puesto valen dos cosas: cada equipo se queda en SU mitad, y
   * el círculo central es solo para quien saca. Tras un gol saca el que lo
   * encajó — si no, el que acaba de marcar se planta encima del balón y el
   * saque no existe como jugada. El primer saque del partido es libre: ahí nadie
   * ha encajado nada.
   *
   * No se aplica durante la pausa de gol (ver `enforceKickoff`) y se limpia solo
   * en cuanto alguien toca la pelota (ver `releaseKickoff`).
   */
  kickoffTeam: TeamId | null;
}


export interface InputState {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  kick: boolean;
  kickRelease: boolean;
  /**
   * Eje ANALÓGICO de movimiento, en centésimas (-100..100), y+ hacia abajo.
   *
   * Es lo que hace que el joystick del móvil mida: con solo cuatro booleanos,
   * rozar el stick y estrujarlo daban exactamente la misma velocidad, así que
   * colocarse con precisión era imposible y todo el mundo iba a tope o parado.
   *
   * Va en ENTEROS y no en flotantes de 0..1 por determinismo: el valor viaja por
   * la red y se divide entre 100 en las dos puntas, así que cliente y servidor
   * hacen la misma cuenta con los mismos bits. Cero (los dos ejes) = "no hay
   * analógico", y mandan los booleanos — que es el caso del teclado.
   */
  mx: number;
  my: number;
}

/** Escala del eje analógico (`mx`/`my` van en centésimas). */
export const ANALOG_SCALE = 100;

export function createInputState(): InputState {
  return { up: false, down: false, left: false, right: false, kick: false, kickRelease: false, mx: 0, my: 0 };
}

const IDLE_INPUT: InputState = createInputState();

export interface CreateGameOptions {
  /** Número de jugadores (cuerpos) del mundo. 1 = local; 2+ = online. */
  players?: number;
  /** Equipo de cada slot (rojo/turquesa). Por defecto alterna 0,1,0,1… */
  teams?: TeamId[];
  /** Slots realmente ocupados. Por defecto TODOS (comportamiento histórico);
   *  la sala los sincroniza con quién hay conectado (ver `setSlotActive`). */
  active?: boolean[];
}

/** Equipo por defecto de un slot cuando no se especifica: alterna rojo/turquesa. */
function defaultTeam(slot: number): TeamId {
  return (slot % 2) as TeamId;
}

/** Jugadores por columna de saque antes de abrir una segunda línea. */
const SPAWN_COLUMN = 4;
/** Separación vertical máxima entre compañeros en el saque. */
const SPAWN_SPACING = 130;

/**
 * Posición de saque de un slot según su equipo y su orden dentro del equipo.
 * Rojo saca a la izquierda del centro, turquesa a la derecha; los compañeros de
 * un mismo equipo se reparten en vertical. Para 1 slot por equipo cae en el eje
 * central (CH/2), de modo que 2 jugadores conservan el spawn histórico ±220.
 *
 * A partir de 5 por equipo se abre una segunda línea (y una tercera, …) hacia
 * la propia portería en vez de seguir estirando la columna: con salas de hasta
 * 30 jugadores, una sola columna colocaba a la mitad del equipo fuera de la
 * cancha, y el saneador de física los recentraba a todos encima del balón.
 */
function teamSpawn(team: TeamId, indexInTeam: number, teamCount: number, CW: number, CH: number): { x: number; y: number } {
  const col = Math.floor(indexInTeam / SPAWN_COLUMN);
  const row = indexInTeam % SPAWN_COLUMN;
  const inCol = Math.min(SPAWN_COLUMN, teamCount - col * SPAWN_COLUMN);
  const dir = team === 0 ? -1 : 1;
  // La línea de fondo nunca se mete dentro del área chica de su propia portería.
  const rawX = CW / 2 + dir * (220 + col * 140);
  const x = Math.max(PLAYER_R * 3, Math.min(CW - PLAYER_R * 3, rawX));
  // El reparto vertical se comprime si la columna no cabe con la separación
  // nominal (canchas bajas con equipos llenos).
  const spacing = Math.min(SPAWN_SPACING, (CH - PLAYER_R * 4) / Math.max(1, inCol));
  const y = CH / 2 + (row - (inCol - 1) / 2) * spacing;
  return { x, y };
}

/**
 * Spawn de un slot según su equipo y su orden entre los compañeros que ESTÁN
 * JUGANDO (no entre los slots reservados).
 *
 * Contar los slots vacíos era el bug: en una sala de 8 con dos personas, cada
 * una era "el primero de cuatro" de su equipo y salía colocada arriba del todo,
 * a 180 px por encima del balón, con el hueco de tres compañeros que no existen
 * debajo. Un 1v1 tiene que sacar EN LA LÍNEA del balón, y un 2v2 repartido en
 * torno a ella; lo que decide eso es cuánta gente hay, no cuánta cabe.
 */
function slotSpawn(teams: TeamId[], slot: number, CW: number, CH: number, active?: readonly boolean[]): { x: number; y: number } {
  const team = teams[slot];
  const playing = (i: number): boolean => active === undefined || active[i] !== false || i === slot;
  const mates = teams.reduce<number[]>((acc, t, i) => {
    if (t === team && playing(i)) acc.push(i);
    return acc;
  }, []);
  const idx = mates.indexOf(slot);
  return teamSpawn(team, idx < 0 ? 0 : idx, Math.max(1, mates.length), CW, CH);
}

export function createGame(
  map: MapConfig = MAPS[0],
  rules: MatchRules = defaultRules(),
  opts: CreateGameOptions = {},
): GameState {
  const numPlayers = Math.max(1, Math.floor(opts.players ?? 1));
  const teams: TeamId[] = Array.from({ length: numPlayers }, (_, i) => opts.teams?.[i] ?? defaultTeam(i));
  const w = createWorld();
  const wallBounce = 0.92;
  const wallFric = 0.04;
  const marginBounce = 0.6;

  const CW = map.courtW;
  const CH = map.courtH;
  const GH = map.goalH;

  const gy0 = (CH - GH) / 2;
  const gy1 = (CH + GH) / 2;
  const sideH = (CH - GH) / 2;

  addBox(w, CW / 2, -WALL_T / 2, CW / 2 + WALL_T, WALL_T / 2, wallBounce, wallFric, LAYER_BALL);
  addBox(w, CW / 2, CH + WALL_T / 2, CW / 2 + WALL_T, WALL_T / 2, wallBounce, wallFric, LAYER_BALL);

  addBox(w, -WALL_T / 2, sideH / 2, WALL_T / 2, sideH / 2, wallBounce, wallFric, LAYER_BALL);
  addBox(w, -WALL_T / 2, gy1 + sideH / 2, WALL_T / 2, sideH / 2, wallBounce, wallFric, LAYER_BALL);
  addBox(w, CW + WALL_T / 2, sideH / 2, WALL_T / 2, sideH / 2, wallBounce, wallFric, LAYER_BALL);
  addBox(w, CW + WALL_T / 2, gy1 + sideH / 2, WALL_T / 2, sideH / 2, wallBounce, wallFric, LAYER_BALL);

  addBox(w, -GOAL_DEPTH - WALL_T / 2, (gy0 + gy1) / 2, WALL_T / 2, GH / 2, wallBounce, wallFric, LAYER_BALL);
  addBox(w, CW + GOAL_DEPTH + WALL_T / 2, (gy0 + gy1) / 2, WALL_T / 2, GH / 2, wallBounce, wallFric, LAYER_BALL);
  // Barras superior/inferior de la red. Su cara del lado del campo termina EXACTO
  // en la línea de gol (x=0 / x=CW): antes se pasaban WALL_T/2 hacia dentro del
  // campo (medio-ancho GOAL_DEPTH/2 + WALL_T/2), creando un tope invisible por
  // encima/debajo del poste contra el que el balón rebotaba sin nada dibujado.
  // Ahora abarcan de la línea de gol hacia atrás (solape trasero para sellar con
  // el backstop), sin invadir el campo; el poste sella la esquina delantera.
  const netBarHalfX = (GOAL_DEPTH + WALL_T / 2) / 2;
  addBox(w, -netBarHalfX, gy0 - WALL_T / 2, netBarHalfX, WALL_T / 2, wallBounce, wallFric, LAYER_BALL);
  addBox(w, -netBarHalfX, gy1 + WALL_T / 2, netBarHalfX, WALL_T / 2, wallBounce, wallFric, LAYER_BALL);
  addBox(w, CW + netBarHalfX, gy0 - WALL_T / 2, netBarHalfX, WALL_T / 2, wallBounce, wallFric, LAYER_BALL);
  addBox(w, CW + netBarHalfX, gy1 + WALL_T / 2, netBarHalfX, WALL_T / 2, wallBounce, wallFric, LAYER_BALL);

  const extL = -MARGIN;
  const extR = CW + MARGIN;
  const extT = -MARGIN;
  const extB = CH + MARGIN;
  addBox(w, (extL + extR) / 2, extT - WALL_T / 2, (extR - extL) / 2 + WALL_T, WALL_T / 2, marginBounce, wallFric, LAYER_PLAYER);
  addBox(w, (extL + extR) / 2, extB + WALL_T / 2, (extR - extL) / 2 + WALL_T, WALL_T / 2, marginBounce, wallFric, LAYER_PLAYER);
  addBox(w, extL - WALL_T / 2, (extT + extB) / 2, WALL_T / 2, (extB - extT) / 2 + WALL_T, marginBounce, wallFric, LAYER_PLAYER);
  addBox(w, extR + WALL_T / 2, (extT + extB) / 2, WALL_T / 2, (extB - extT) / 2 + WALL_T, marginBounce, wallFric, LAYER_PLAYER);

  const postBounce = 0.85;
  const postFric = 0.04;
  const pL0 = addCircle(w, 0, gy0, POST_R, 0, postBounce, postFric, 0, LAYER_ALL);
  const pL1 = addCircle(w, 0, gy1, POST_R, 0, postBounce, postFric, 0, LAYER_ALL);
  const pR0 = addCircle(w, CW, gy0, POST_R, 0, postBounce, postFric, 0, LAYER_ALL);
  const pR1 = addCircle(w, CW, gy1, POST_R, 0, postBounce, postFric, 0, LAYER_ALL);

  // Orden de creación de cuerpos PRESERVADO para N≤2 (player, ball, player2) para
  // no alterar la resolución de colisiones byte-a-byte de los tests deterministas;
  // los slots extra (3+) se añaden después. Los spawns se calculan por equipo.
  const spawn = (slot: number): { x: number; y: number } =>
    numPlayers === 1 ? { x: CW / 2, y: CH / 2 } /* 1 jugador: centro histórico */
      : slotSpawn(teams, slot, CW, CH, opts.active);

  const s0 = spawn(0);
  const player = addCircle(w, s0.x, s0.y, PLAYER_R, PLAYER_MASS, 0.2, 0.05, PLAYER_DRAG, LAYER_PLAYER);
  const ballY = numPlayers >= 2 ? CH / 2 : CH / 2 - 100;
  const ball = addCircle(w, CW / 2, ballY, BALL_R, BALL_MASS, 0.98, 0.02, BALL_DRAG, LAYER_BALL | LAYER_PLAYER);

  const players: number[] = [player];
  for (let slot = 1; slot < numPlayers; slot++) {
    const s = spawn(slot);
    players.push(addCircle(w, s.x, s.y, PLAYER_R, PLAYER_MASS, 0.2, 0.05, PLAYER_DRAG, LAYER_PLAYER));
  }
  const player2 = players[1] ?? -1;
  const active = players.map((_, slot) => opts.active?.[slot] ?? true);

  const g: GameState = {
    world: w, player, player2, players, active, teams, ball, posts: [pL0, pL1, pR0, pR1],
    score: [0, 0], paused: false,
    charges: players.map(() => 0),
    chargings: players.map(() => false),
    possession: players.map(() => 0),
    kickHeld: players.map(() => false),
    kickArmed: players.map(() => false),
    kickPulses: players.map(() => 0),
    kickInfos: players.map(() => makeKickInfo()),
    goalAnim: 0, goalSide: null, resetTimer: 0, events: [], lastBallSpeed: 0,
    courtW: CW, courtH: CH, goalH: GH, mapId: map.id,
    rules: { goalLimit: rules.goalLimit, timeLimit: rules.timeLimit },
    matchTime: 0, matchOver: false, winner: null, overtime: false,
    // Primer saque del partido: libre para los dos (nadie ha encajado nada).
    kickoffTeam: null,
  };
  // Los slots que nacen vacíos se aparcan fuera del campo desde el primer tick:
  // así una sala de 8 con 2 personas dentro es una cancha con 2 discos.
  for (let slot = 0; slot < numPlayers; slot++) if (!g.active[slot]) benchSlot(g, slot);
  return g;
}

/**
 * Aparca el cuerpo de un slot vacío fuera del campo y sin colisión.
 *
 * Se le quita la máscara (no choca con nada) y se le manda detrás del margen,
 * lejos de la cancha pero MUY dentro del cuadro que vigila el saneador de
 * física (`sanitizeGameState`), que si no lo recentraría encima del balón.
 */
function benchSlot(g: GameState, slot: number): void {
  const w = g.world;
  const b = g.players[slot];
  if (b === undefined || b < 0) return;
  w.mask[b] = LAYER_NONE;
  w.px[b] = -MARGIN * 2 - (slot % 8) * (PLAYER_R * 3);
  w.py[b] = -MARGIN * 2 - Math.floor(slot / 8) * (PLAYER_R * 3);
  w.vx[b] = 0;
  w.vy[b] = 0;
  g.charges[slot] = 0;
  g.chargings[slot] = false;
  g.possession[slot] = 0;
  g.kickHeld[slot] = false;
  g.kickArmed[slot] = false;
  g.kickPulses[slot] = 0;
}

/**
 * Marca un slot como ocupado (o vacío) y recoloca su disco en consecuencia.
 *
 * Autoritativo: lo llama el servidor cuando alguien entra o sale de la sala, y
 * el cliente al recibirlo en el snapshot. Devuelve `true` si cambió algo.
 */
export function setSlotActive(g: GameState, slot: number, on: boolean): boolean {
  if (slot < 0 || slot >= g.players.length) return false;
  if (g.active[slot] === on) return false;
  g.active[slot] = on;
  if (!on) { benchSlot(g, slot); return true; }
  const w = g.world;
  const b = g.players[slot];
  // Ojo al orden: `g.active[slot]` ya está en `true` aquí, así que quien acaba
  // de entrar cuenta como compañero y el reparto sale simétrico.
  const s = slotSpawn(g.teams, slot, g.courtW, g.courtH, g.active);
  w.mask[b] = LAYER_PLAYER;
  w.px[b] = s.x;
  w.py[b] = s.y;
  w.vx[b] = 0;
  w.vy[b] = 0;
  return true;
}

/** Sincroniza de golpe qué slots están ocupados. Devuelve `true` si cambió alguno. */
export function setActiveSlots(g: GameState, active: readonly boolean[]): boolean {
  let changed = false;
  for (let slot = 0; slot < g.players.length; slot++) {
    if (setSlotActive(g, slot, active[slot] ?? false)) changed = true;
  }
  return changed;
}

/** Referencia mutable al control de un jugador (slot 0 o 1) para no duplicar lógica. */
interface PlayerRef {
  body: number;
  kickInfo: KickInfo;
  getCharge(): number;
  setCharge(v: number): void;
  getCharging(): boolean;
  setCharging(v: boolean): void;
  getPossession(): number;
  setPossession(v: number): void;
  getKickHeld(): boolean;
  setKickHeld(v: boolean): void;
  getKickArmed(): boolean;
  setKickArmed(v: boolean): void;
  getKickPulse(): number;
  setKickPulse(v: number): void;
}

function refFor(g: GameState, slot: number): PlayerRef {
  return {
    body: g.players[slot], kickInfo: g.kickInfos[slot],
    getCharge: () => g.charges[slot], setCharge: (v) => { g.charges[slot] = v; },
    getCharging: () => g.chargings[slot], setCharging: (v) => { g.chargings[slot] = v; },
    getPossession: () => g.possession[slot], setPossession: (v) => { g.possession[slot] = v; },
    getKickHeld: () => g.kickHeld[slot], setKickHeld: (v) => { g.kickHeld[slot] = v; },
    getKickArmed: () => g.kickArmed[slot], setKickArmed: (v) => { g.kickArmed[slot] = v; },
    getKickPulse: () => g.kickPulses[slot], setKickPulse: (v) => { g.kickPulses[slot] = v; },
  };
}

/** Distancia centro-a-centro del balón a un disco. */
function ballDistance(g: GameState, body: number): number {
  const w = g.world;
  return length2(w.px[g.ball] - w.px[body], w.py[g.ball] - w.py[body]);
}

/**
 * Posesión y patada de un jugador.
 *
 * - POSESIÓN: mientras el balón esté dentro de `POSSESSION_RANGE`, el jugador
 *   "lo tiene" y acumula segundos (tope `POSSESSION_TIME_MAX`); si lo pierde, se
 *   resetea a 0. El ratio 0..1 alimenta el aro del HUD (`charges`).
 *   La carga se CANCELA si otro jugador disputa el balón (ver `contested`): la
 *   posesión es tener la pelota, no estar cerca de ella mientras el rival la
 *   toca.
 * - PATADA: se dispara con la tecla PULSADA, no solo en su flanco de subida.
 *   Mantenerla es "voy a golpear en cuanto la alcance": el balón sale en el tick
 *   en que entra en `KICK_RANGE`. Es lo que hace jugable el móvil (donde acertar
 *   el instante del toque con el pulgar es una lotería) y lo que se ve venir,
 *   porque el borde del disco está encendido mientras se mantiene.
 */
function possessAndKick(g: GameState, ref: PlayerRef, input: InputState, dt: number): void {
  const hadPossession = ref.getCharging();
  const dist = ballDistance(g, ref.body);
  const inRange = dist <= POSSESSION_RANGE;
  // Otro jugador la está tocando: nadie carga. Se mira ANTES de acumular, para
  // que el tick en que llega el rival ya no sume.
  const disputed = inRange && contested(g, ref.body);

  if (inRange && !disputed) ref.setPossession(Math.min(POSSESSION_TIME_MAX, ref.getPossession() + dt));
  else ref.setPossession(0);
  ref.setCharging(inRange && !disputed);
  ref.setCharge(ref.getPossession() / POSSESSION_TIME_MAX);

  // UNA patada por pulsación. Mantener sigue significando "golpeo en cuanto la
  // alcance" (imprescindible con el pulgar), pero en cuanto sale, el gatillo se
  // DESARMA hasta soltar. Sin eso, mantener pulsado disparaba en CADA tick en
  // que el balón estuviera en alcance: una ametralladora de toques mínimos que
  // se sentía como "la pelota no sale" o "se me devuelve" — porque cada re-toque
  // reiniciaba la carga y la pelota nunca llegaba a salir con fuerza.
  const held = ref.getKickHeld();
  const armed = input.kick && !held ? true : input.kick ? ref.getKickArmed() : false;
  if (armed && kick(g, ref, ref.getCharge())) {
    g.events.push("kick");
    ref.setKickPulse(1);
    ref.setPossession(0);
    ref.setCharge(0);
    ref.setCharging(false);
    ref.setKickArmed(false); // gastado: hay que soltar y volver a pulsar
  } else {
    ref.setKickArmed(armed);
  }
  ref.setKickHeld(input.kick);

  if (!hadPossession && ref.getCharging()) g.events.push("chargeStart");
  if (hadPossession && !ref.getCharging()) g.events.push("chargeStop");
  if (input.kickRelease) input.kickRelease = false;
  if (ref.getKickPulse() > 0) ref.setKickPulse(Math.max(0, ref.getKickPulse() - dt * 4));
}

/** ¿Hay OTRO jugador (activo) con el balón dentro de su radio de disputa? */
function contested(g: GameState, body: number): boolean {
  for (let i = 0; i < g.players.length; i++) {
    const other = g.players[i];
    if (other === body || other < 0 || g.active[i] === false) continue;
    if (ballDistance(g, other) <= CONTEST_RANGE) return true;
  }
  return false;
}

/**
 * Fuerza de movimiento de un jugador según su input.
 *
 * Dos fuentes, misma salida: si hay eje analógico (`mx`/`my`, el joystick táctil)
 * manda su MAGNITUD — medio stick es media fuerza — y si no, los cuatro
 * booleanos del teclado, que siempre van a tope. Un teclado no tiene medias
 * tintas, así que fingirlas ahí sería inventarse una precisión que no existe.
 */
function movePlayer(g: GameState, ref: PlayerRef, input: InputState, dt: number): void {
  let ix = 0;
  let iy = 0;
  // `?? 0` y no `input.mx`: hay inputs construidos a mano (tests, bots, clientes
  // de una versión anterior por la red) que no traen los ejes. Ausente = cero =
  // "no hay analógico", que es exactamente lo que significa.
  const ax = input.mx ?? 0;
  const ay = input.my ?? 0;
  if (ax !== 0 || ay !== 0) {
    ix = ax / ANALOG_SCALE;
    iy = ay / ANALOG_SCALE;
    const len = length2(ix, iy);
    // Solo se normaliza si el vector se pasa del círculo unidad: por dentro, la
    // magnitud ES la orden (moverse despacio es una decisión del jugador).
    if (len > 1) { ix /= len; iy /= len; }
  } else {
    if (input.up) iy -= 1;
    if (input.down) iy += 1;
    if (input.left) ix -= 1;
    if (input.right) ix += 1;
    if (ix !== 0 || iy !== 0) {
      const len = length2(ix, iy);
      ix /= len;
      iy /= len;
    }
  }
  if (ix !== 0 || iy !== 0) {
    // Sin penalización de velocidad: tener posesión (llevar el balón) no frena.
    applyForce(g.world, ref.body, ix * PLAYER_ACCEL_FORCE, iy * PLAYER_ACCEL_FORCE, dt);
  }
}

/**
 * Avanza un tick con el input de CADA slot (`inputs[i]` para el slot i; los que
 * falten quedan en idle). Es la forma general (N jugadores); `updateGame` es el
 * envoltorio de 1-2 jugadores por compatibilidad.
 *
 * El orden de operaciones (carga/kick de todos los slots en orden → decays →
 * movimiento de todos → step físico → clamps) se conserva idéntico al histórico
 * para N≤2, de modo que la simulación es byte-a-byte la misma.
 */
export function updateGameMulti(g: GameState, inputs: InputState[], dt: number): void {
  const w = g.world;
  const n = g.players.length;
  g.events.length = 0;
  for (let i = 0; i < n; i++) g.kickInfos[i].active = false;

  if (g.matchOver) return;

  if (g.rules.timeLimit > 0 && g.resetTimer <= 0) {
    g.matchTime += dt;
    if (!g.overtime && g.matchTime >= g.rules.timeLimit) {
      // GP-1 · Se acabó el tiempo. Con empate NO se termina: un partido que
      // acaba en tablas no decide nada, y decidir es para lo que se juega. Entra
      // la PRÓRROGA a gol de oro — el siguiente gol cierra el partido, venga de
      // quien venga. El reloj sigue corriendo para que se vea cuánto dura.
      if (g.score[0] === g.score[1]) {
        g.overtime = true;
        g.events.push("overtime");
      } else {
        g.matchTime = g.rules.timeLimit;
        finishMatch(g);
        return;
      }
    }
  }

  const ballSpBefore = length2(w.vx[g.ball], w.vy[g.ball]);

  // Un slot vacío no juega: ni recibe input, ni tiene posesión, ni patea. Su
  // disco está aparcado fuera del campo (ver `setSlotActive`).
  const inputOf = (i: number): InputState => (g.active[i] === false ? IDLE_INPUT : inputs[i] ?? IDLE_INPUT);
  const refs: PlayerRef[] = [];
  for (let i = 0; i < n; i++) refs.push(refFor(g, i));

  for (let i = 0; i < n; i++) if (g.active[i] !== false) possessAndKick(g, refs[i], inputOf(i), dt);

  if (g.goalAnim > 0) g.goalAnim = Math.max(0, g.goalAnim - dt / GOAL_ANIM_DURATION);
  if (g.resetTimer > 0) {
    g.resetTimer = Math.max(0, g.resetTimer - dt);
    if (g.resetTimer === 0) resetRound(g);
  }

  for (let i = 0; i < n; i++) if (g.active[i] !== false) movePlayer(g, refs[i], inputOf(i), dt);

  step(w, dt);

  // Reglas del SAQUE: cada equipo en su mitad, y el círculo solo para quien
  // saca. Se aplican DESPUÉS del paso físico —como restricciones de posición, no
  // como fuerzas— para que sean exactas: una fuerza se puede vencer empujando
  // más, y entonces la regla es una sugerencia.
  enforceKickoff(g);
  releaseKickoff(g);

  clampSpeed(w, g.ball, BALL_MAX_SPEED);
  for (let i = 0; i < n; i++) clampSpeed(w, g.players[i], PLAYER_MAX_SPEED * 1.25);

  const ballSpAfter = length2(w.vx[g.ball], w.vy[g.ball]);
  if (!g.events.includes("kick")) {
    const delta = Math.abs(ballSpAfter - ballSpBefore);
    if (delta > 60 && ballSpAfter > 40) {
      if (hitPosts(g)) g.events.push("post");
      else g.events.push("bounce");
    }
  }
  g.lastBallSpeed = ballSpAfter;

  checkGoal(g);
}

/**
 * Envoltorio de compatibilidad (1-2 jugadores): `updateGame(g, in0, dt, in1?)`.
 * Reenvía a `updateGameMulti`. Todos los llamadores históricos (local, cliente
 * predictivo de 1-2 slots, tests) siguen funcionando sin cambios.
 */
export function updateGame(g: GameState, input: InputState, dt: number, input2?: InputState): void {
  updateGameMulti(g, input2 !== undefined ? [input, input2] : [input], dt);
}

/**
 * Restricciones del saque: cada equipo en SU mitad, y el círculo para quien saca.
 *
 * Son dos reglas y hacen falta las dos. Sin la mitad, el saque se juega con los
 * dos bandos ya mezclados y no llega a ser una jugada: es el forcejeo de
 * siempre, empezado un metro más allá. Con ella, el que saca tiene su campo
 * entero para elegir a dónde va la pelota — que es lo que un saque significa.
 *
 * Se aplican DESPUÉS del paso físico —como restricciones de posición, no como
 * fuerzas— para que sean exactas: una fuerza se puede vencer empujando más, y
 * entonces la regla es una sugerencia. En los dos casos se devuelve al disco al
 * borde y se le anula solo la componente de velocidad que cruzaba; lo tangente
 * se conserva, así que moverse a lo largo del límite se siente natural y no como
 * chocar contra un muro invisible.
 */
function enforceKickoff(g: GameState): void {
  const team = g.kickoffTeam;
  if (team === null) return;
  // NADA de esto durante la pausa de gol.
  //
  // El que acaba de marcar está, por definición, dentro del área contraria: es
  // donde se marca. Aplicar ahí la regla lo arrastraba de un tirón hasta el
  // centro del campo en el instante de celebrar el gol —cientos de píxeles, con
  // el disco cruzando media cancha solo—, y al que encajó lo empujaba fuera del
  // círculo desde donde estuviera. La restricción empieza cuando empieza el
  // saque: con la pelota ya en el centro y todo el mundo colocado en su sitio
  // por `resetRound`, que son posiciones legales. Desde ahí la regla solo
  // IMPIDE avanzar; nunca teletransporta a nadie.
  if (g.resetTimer > 0) return;
  // Partida de práctica (un solo disco): no hay dos campos que repartir.
  if (g.players.length < 2) return;
  const w = g.world;
  const cx = g.courtW / 2;
  const cy = g.courtH / 2;
  for (let i = 0; i < g.players.length; i++) {
    if (g.active[i] === false) continue;
    const b = g.players[i];
    if (b < 0) continue;
    // 1 · Cada uno en su mitad. El equipo 0 ataca hacia la derecha y sale desde
    // la izquierda (ver `teamSpawn`), así que su campo es el de la izquierda.
    // El límite deja el disco TOCANDO la línea, no detrás: desde ahí se llega
    // al balón, que está justo en el centro. Un saque desde el que no se puede
    // tocar la pelota no sería un saque.
    const dir = g.teams[i] === 0 ? -1 : 1;
    const limit = cx + dir * w.radius[b];
    if (dir < 0 ? w.px[b] > limit : w.px[b] < limit) {
      w.px[b] = limit;
      if (dir < 0 ? w.vx[b] > 0 : w.vx[b] < 0) w.vx[b] = 0;
    }
    // 2 · El círculo, solo para el equipo que saca.
    if (g.teams[i] === team) continue;
    const dx = w.px[b] - cx;
    const dy = w.py[b] - cy;
    const d = length2(dx, dy);
    const min = KICKOFF_RADIUS + w.radius[b];
    if (d >= min) continue;
    // Justo en el centro (caso degenerado): se le saca por su propio lado.
    const nx = d > 1e-6 ? dx / d : (g.teams[i] === 0 ? -1 : 1);
    const ny = d > 1e-6 ? dy / d : 0;
    w.px[b] = cx + nx * min;
    w.py[b] = cy + ny * min;
    const inward = w.vx[b] * nx + w.vy[b] * ny;
    if (inward < 0) {
      w.vx[b] -= inward * nx;
      w.vy[b] -= inward * ny;
    }
  }
}

/**
 * Levanta la restricción del saque en cuanto la pelota entra en juego: alguien
 * la tiene al alcance, o ya se ha movido del centro. A partir de ahí es una
 * jugada normal y el círculo no pinta nada.
 */
function releaseKickoff(g: GameState): void {
  if (g.kickoffTeam === null) return;
  // NADIE puede sacar: no hay un solo jugador del equipo con derecho al saque
  // (una sala con un equipo vacío, o alguien practicando solo online). Mantener
  // la restricción ahí deja la pelota clavada en el centro PARA SIEMPRE: los que
  // están no pueden acercarse al círculo y no hay nadie más que la toque. Una
  // regla que puede dejar la partida sin salida no es una regla, es un cuelgue.
  let alguien = false;
  for (let i = 0; i < g.players.length; i++) {
    if (g.active[i] !== false && g.teams[i] === g.kickoffTeam) { alguien = true; break; }
  }
  if (!alguien) { g.kickoffTeam = null; return; }
  // Durante la pausa de gol el balón todavía está dentro de la portería, así que
  // "se ha movido del centro" es trivialmente cierto y la restricción se caía
  // antes de existir. El saque empieza a contar cuando la pelota vuelve al
  // centro, no mientras se celebra.
  if (g.resetTimer > 0) return;
  const w = g.world;
  const b = g.ball;
  const moved = length2(w.px[b] - g.courtW / 2, w.py[b] - g.courtH / 2) > 2
    || length2(w.vx[b], w.vy[b]) > 1;
  if (moved) { g.kickoffTeam = null; return; }
  for (let i = 0; i < g.players.length; i++) {
    if (g.active[i] === false) continue;
    if (ballDistance(g, g.players[i]) <= POSSESSION_RANGE) { g.kickoffTeam = null; return; }
  }
}

function hitPosts(g: GameState): boolean {
  const w = g.world;
  const b = g.ball;
  for (const post of g.posts) {
    const dx = w.px[b] - w.px[post];
    const dy = w.py[b] - w.py[post];
    const r = w.radius[b] + w.radius[post];
    if (dx * dx + dy * dy < r * r) return true;
  }
  return false;
}

function clampSpeed(w: World, i: number, max: number): void {
  const sp = length2(w.vx[i], w.vy[i]);
  if (sp > max) {
    const k = max / sp;
    w.vx[i] *= k;
    w.vy[i] *= k;
  }
}

/**
 * Cambia el equipo de un slot y reposiciona a todos (saque nuevo). Autoritativo:
 * lo llama el servidor cuando el admin arrastra a un jugador entre equipos. No-op
 * si el slot no existe o ya está en ese equipo. Devuelve `true` si cambió algo.
 */
/**
 * Adopta el reparto de equipos que dicta OTRO (el servidor), sin tocar a nadie.
 *
 * Es la versión para un cliente que predice. `setSlotTeam` recoloca a todo el
 * mundo, que es lo correcto cuando la decisión se toma aquí; en un cliente sería
 * un teletransporte por cada mensaje de sala.
 *
 * Hacía falta porque el cliente NO sabía de qué equipo era nadie: nacía con el
 * reparto por defecto (0,1,0,1…) y ahí se quedaba. Los colores salían bien
 * porque el render los saca de la lista de la sala, así que el fallo no se veía
 * —hasta que hubo reglas que dependen del equipo. Con el saque, un cliente que
 * se creía del bando contrario se encerraba solo en la mitad equivocada y se
 * apartaba del círculo mientras el servidor le dejaba jugar: el disco peleando
 * contra su propia predicción.
 */
export function adoptTeams(g: GameState, teams: readonly (TeamId | undefined)[]): boolean {
  let changed = false;
  for (let slot = 0; slot < g.teams.length; slot++) {
    const t = teams[slot];
    if (t === undefined || g.teams[slot] === t) continue;
    g.teams[slot] = t;
    changed = true;
  }
  return changed;
}

export function setSlotTeam(g: GameState, slot: number, team: TeamId): boolean {
  if (slot < 0 || slot >= g.teams.length) return false;
  if (g.teams[slot] === team) return false;
  g.teams[slot] = team;
  resetRound(g); // recoloca a ambos equipos y reinicia el saque
  return true;
}

/**
 * Rapidez de saque (px/s) para llevar el balón `distance` px hasta frenar, bajo el
 * modelo de arrastre discreto del solver (`v *= 1 - drag·dt` cada tick). Distancia
 * recorrida ≈ v·(1 − drag·dt)/drag, así que se invierte para hallar la v inicial.
 */
function speedForDistance(distance: number): number {
  const f = 1 - BALL_DRAG * SIM_DT;
  return (distance * BALL_DRAG) / f;
}

/**
 * Rapidez del balón tras una patada a MÁXIMA carga en una cancha de este ancho.
 *
 * Se exporta para el render, no para el sim: es la referencia con la que se
 * decide si el balón va "rápido" —la estela del balón crece con ella— y ese
 * número no puede vivir escrito a mano en el dibujante. La fuerza de la patada
 * depende del ancho de la cancha (`KICK_CROSS_MARGIN`) y del arrastre, así que
 * un valor copiado se queda viejo en cuanto se retoque cualquiera de los dos, y
 * la estela pasaría a estar siempre al máximo o a no salir nunca. Ninguna de las
 * dos cosas falla; las dos se ven mal.
 */
export function maxKickSpeed(courtW: number): number {
  return speedForDistance(courtW * KICK_CROSS_MARGIN);
}

function kick(g: GameState, ref: PlayerRef, charge: number): boolean {
  const w = g.world;
  const p = ref.body;
  const b = g.ball;
  const dx = w.px[b] - w.px[p];
  const dy = w.py[b] - w.py[p];
  const dist = length2(dx, dy);
  if (dist > KICK_RANGE) return false;
  const nx = dist > 1e-9 ? dx / dist : 1;
  const ny = dist > 1e-9 ? dy / dist : 0;
  // Fuerza (rapidez de saque) según posesión: a máxima carga el balón cruza la
  // cancha de arco a arco (ancho actual) con un margen para entrar en la portería.
  const vMax = speedForDistance(g.courtW * KICK_CROSS_MARGIN);
  const speed = KICK_SPEED_MIN + (vMax - KICK_SPEED_MIN) * charge;
  // applyImpulse suma impulso·invMass a la velocidad; impulso = speed·BALL_MASS ⇒
  // el saque añade exactamente `speed` px/s en la dirección de pateo.
  applyImpulse(g.world, b, nx * speed * BALL_MASS, ny * speed * BALL_MASS);
  ref.kickInfo.x = w.px[b] - nx * BALL_R;
  ref.kickInfo.y = w.py[b] - ny * BALL_R;
  ref.kickInfo.nx = nx;
  ref.kickInfo.ny = ny;
  ref.kickInfo.intensity = 0.4 + charge * 0.6;
  ref.kickInfo.active = true;
  return true;
}

/**
 * Coloca a todo el mundo (y al balón) como en un saque, sin tocar marcador ni
 * reloj.
 *
 * Se exporta porque la SALA lo necesita: el reparto de saque depende de cuánta
 * gente hay dentro (ver `slotSpawn`), así que cuando alguien entra o sale antes
 * de empezar hay que recolocar. Sin esto, los que ya estaban conservaban la
 * posición que se calculó cuando la sala parecía tener otra plantilla — en una
 * sala de 8 con dos personas, los dos discos salían 180 px por encima del balón.
 */
export function resetKickoff(g: GameState): void {
  resetRound(g);
}

function resetRound(g: GameState): void {
  const w = g.world;
  const n = g.players.length;
  if (n === 1) {
    // 1 jugador (práctica local): posiciones históricas de saque.
    w.px[g.ball] = g.courtW / 2;
    w.py[g.ball] = g.courtH / 2 - 100;
    w.px[g.player] = g.courtW / 2;
    w.py[g.player] = g.courtH / 2 + 100;
    w.vx[g.player] = 0;
    w.vy[g.player] = 0;
  } else {
    w.px[g.ball] = g.courtW / 2;
    w.py[g.ball] = g.courtH / 2;
    for (let i = 0; i < n; i++) {
      if (g.active[i] === false) { benchSlot(g, i); continue; } // sigue aparcado
      const s = slotSpawn(g.teams, i, g.courtW, g.courtH, g.active);
      const b = g.players[i];
      w.px[b] = s.x;
      w.py[b] = s.y;
      w.vx[b] = 0;
      w.vy[b] = 0;
    }
  }
  w.vx[g.ball] = 0;
  w.vy[g.ball] = 0;
  for (let i = 0; i < n; i++) {
    g.charges[i] = 0;
    g.chargings[i] = false;
    g.possession[i] = 0;
    g.kickHeld[i] = false;
    g.kickArmed[i] = false;
    g.kickPulses[i] = 0;
  }
}

function checkGoal(g: GameState): void {
  if (g.resetTimer > 0) return;
  const w = g.world;
  const b = g.ball;
  const x = w.px[b];
  const y = w.py[b];
  const gy0 = (g.courtH - g.goalH) / 2;
  const gy1 = (g.courtH + g.goalH) / 2;
  let scored: 0 | 1 | null = null;
  // La pelota tiene que haber cruzado la línea ENTERA, no rozarla con el centro.
  // Es la regla del fútbol y, sobre todo, es la que se puede ver: con el centro
  // bastaba, así que un balón medio fuera ya era gol y desde la cámara parecía
  // que había entrado sin entrar. Ahora el gol se canta cuando el disco está
  // completamente detrás de la línea, que es justo lo que se ve.
  const r = w.radius[b];
  if (y > gy0 && y < gy1) {
    if (x < -r) scored = 1;
    else if (x > g.courtW + r) scored = 0;
  }
  if (scored !== null) {
    g.score[scored]++;
    // Saca el que ENCAJÓ. Hasta que se toque la pelota, el que marcó no puede
    // entrar en el círculo: si no, se planta encima del balón y el saque deja de
    // ser una jugada para convertirse en un forcejeo.
    g.kickoffTeam = (scored === 0 ? 1 : 0) as TeamId;
    g.events.push("goal");
    g.goalAnim = 1;
    g.goalSide = scored;
    g.resetTimer = GOAL_FLASH_DURATION;
    // En prórroga cualquier gol es el último: es un gol de oro (GP-1).
    if (g.overtime || (g.rules.goalLimit > 0 && g.score[scored] >= g.rules.goalLimit)) {
      finishMatch(g);
    }
  }
}

function finishMatch(g: GameState): void {
  g.matchOver = true;
  g.winner = g.score[0] === g.score[1] ? null : g.score[0] > g.score[1] ? 0 : 1;
  for (let i = 0; i < g.players.length; i++) {
    g.chargings[i] = false;
    g.charges[i] = 0;
    g.possession[i] = 0;
    g.kickHeld[i] = false;
    g.kickArmed[i] = false;
  }
  g.events.push("matchEnd");
}
